// src/components/Alerts/AlertSettingsPanel.js
// Settings panel for configuring alert thresholds
// Used in both Firm Portal (firm-wide) and User Portal (personal overrides)

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Divider,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip
} from '@mui/material';
import {
    Save as SaveIcon,
    Restore as RestoreIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useAlertSettings } from '../../hooks/useClientAlerts';
import { DEFAULT_ALERT_THRESHOLDS } from '../../utils/alertUtils';
import { toast } from 'react-toastify';

/**
 * AlertSettingsPanel - Configure alert thresholds
 * 
 * Props:
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID (optional - for user portal)
 * @param {boolean} isFirmLevel - Whether this is firm-level settings (vs user override)
 * @param {object} user - Current user object
 */
const AlertSettingsPanel = ({ 
    organizationId, 
    userId = null, 
    isFirmLevel = true,
    user,
    showAsana = false
}) => {
    const {
        firmSettings,
        userSettings,
        loading,
        saving,
        saveFirmSettings,
        saveUserSettings,
        defaults
    } = useAlertSettings(organizationId, userId);

    // Local form state
    const [strategicDays, setStrategicDays] = useState('');
    const [financialsDays, setFinancialsDays] = useState('');
    const [asanaDays, setAsanaDays] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

     // Initialize form from settings - only once when loading completes
    const initialized = useRef(false);
    
    useEffect(() => {
        if (!loading && !initialized.current) {
            initialized.current = true;
            if (isFirmLevel) {
                setStrategicDays(firmSettings.strategicContextDays?.toString() || '');
                setFinancialsDays(firmSettings.financialsDays?.toString() || '');
                setAsanaDays(firmSettings.asanaDays?.toString() || '');
            } else {
                setStrategicDays(userSettings.strategicContextDays?.toString() || '');
                setFinancialsDays(userSettings.financialsDays?.toString() || '');
                setAsanaDays(userSettings.asanaDays?.toString() || '');
            }
        }
    }, [loading, firmSettings, userSettings, isFirmLevel]);

    // Track changes
    useEffect(() => {
        if (loading) return;
        
        const currentSettings = isFirmLevel ? firmSettings : userSettings;
        const originalStrategic = currentSettings.strategicContextDays?.toString() || '';
        const originalFinancials = currentSettings.financialsDays?.toString() || '';
        const originalAsana = currentSettings.asanaDays?.toString() || '';
        
        setHasChanges(
            strategicDays !== originalStrategic ||
            financialsDays !== originalFinancials ||
            asanaDays !== originalAsana
        );
    }, [strategicDays, financialsDays, asanaDays, firmSettings, userSettings, isFirmLevel, loading]);

    const handleSave = async () => {
        const newSettings = {};
        
        // Only include values that are actually set
        if (strategicDays.trim()) {
            const days = parseInt(strategicDays, 10);
            if (isNaN(days) || days < 1 || days > 365) {
                toast.error('Strategic Context threshold must be between 1 and 365 days');
                return;
            }
            newSettings.strategicContextDays = days;
        }
        
        if (financialsDays.trim()) {
            const days = parseInt(financialsDays, 10);
            if (isNaN(days) || days < 1 || days > 365) {
                toast.error('Financials threshold must be between 1 and 365 days');
                return;
            }
            newSettings.financialsDays = days;
        }

        if (asanaDays.trim()) {
            const days = parseInt(asanaDays, 10);
            if (isNaN(days) || days < 1 || days > 365) {
                toast.error('Asana threshold must be between 1 and 365 days');
                return;
            }
            newSettings.asanaDays = days;
        }

        const success = isFirmLevel 
            ? await saveFirmSettings(newSettings)
            : await saveUserSettings(newSettings);

        if (success) {
            toast.success('Alert settings saved');
        } else {
            toast.error('Failed to save settings');
        }
    };

     const handleReset = () => {
        if (isFirmLevel) {
            setStrategicDays(defaults.strategicContextDays.toString());
            setFinancialsDays(defaults.financialsDays.toString());
            setAsanaDays(defaults.asanaDays.toString());
        } else {
            // For user level, clear to use firm defaults
            setStrategicDays('');
            setFinancialsDays('');
            setAsanaDays('');
        }
    };

    const handleDiscard = () => {
        const currentSettings = isFirmLevel ? firmSettings : userSettings;
        setStrategicDays(currentSettings.strategicContextDays?.toString() || '');
        setFinancialsDays(currentSettings.financialsDays?.toString() || '');
        setAsanaDays(currentSettings.asanaDays?.toString() || '');
    };

    if (loading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={24} />
                    <Typography>Loading alert settings...</Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                    <Typography variant="h6" gutterBottom>
                        {isFirmLevel ? 'Firm-Wide Alert Settings' : 'Alert Thresholds'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isFirmLevel 
                            ? 'Set default alert thresholds for all users in your firm. Individual users can override these in their settings.'
                            : 'Configure when you receive alerts about outdated client information. Leave blank to use firm defaults.'
                        }
                    </Typography>
                </Box>
                <Tooltip title={isFirmLevel ? "Reset to system defaults" : "Clear to use firm defaults"}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                </Tooltip>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Strategic Context Threshold */}
                <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Strategic Context Alert
                        <Tooltip title="Alert when questionnaire or Strategic Pulse hasn't been updated within this many days">
                            <InfoIcon fontSize="small" color="action" />
                        </Tooltip>
                    </Typography>
                    <TextField
                        type="number"
                        size="small"
                        value={strategicDays}
                        onChange={(e) => setStrategicDays(e.target.value)}
                        placeholder={isFirmLevel ? defaults.strategicContextDays.toString() : (firmSettings.strategicContextDays || defaults.strategicContextDays).toString()}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">days</InputAdornment>,
                        }}
                        inputProps={{ min: 1, max: 365 }}
                        sx={{ width: 150 }}
                        helperText={
                            !isFirmLevel && !strategicDays.trim() 
                                ? `Using firm default: ${firmSettings.strategicContextDays || defaults.strategicContextDays} days`
                                : null
                        }
                    />
                </Box>

                 {/* Financials Threshold */}
                <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Financials Alert
                        <Tooltip title="Alert when client financials haven't been updated within this many days">
                            <InfoIcon fontSize="small" color="action" />
                        </Tooltip>
                    </Typography>
                    <TextField
                        type="number"
                        size="small"
                        value={financialsDays}
                        onChange={(e) => setFinancialsDays(e.target.value)}
                        placeholder={isFirmLevel ? defaults.financialsDays.toString() : (firmSettings.financialsDays || defaults.financialsDays).toString()}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">days</InputAdornment>,
                        }}
                        inputProps={{ min: 1, max: 365 }}
                        sx={{ width: 150 }}
                        helperText={
                            !isFirmLevel && !financialsDays.trim() 
                                ? `Using firm default: ${firmSettings.financialsDays || defaults.financialsDays} days`
                                : null
                        }
                    />
                </Box>

                {/* Asana Threshold - only show if any client/user has Asana */}
                {showAsana && (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Asana Alert
                            <Tooltip title="Alert when Asana data hasn't been synced within this many days">
                                <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                        </Typography>
                        <TextField
                            type="number"
                            size="small"
                            value={asanaDays}
                            onChange={(e) => setAsanaDays(e.target.value)}
                            placeholder={isFirmLevel ? defaults.asanaDays.toString() : (firmSettings.asanaDays || defaults.asanaDays).toString()}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">days</InputAdornment>,
                            }}
                            inputProps={{ min: 1, max: 365 }}
                            sx={{ width: 150 }}
                            helperText={
                                !isFirmLevel && !asanaDays.trim() 
                                    ? `Using firm default: ${firmSettings.asanaDays || defaults.asanaDays} days`
                                    : null
                            }
                        />
                    </Box>
                )}
            </Box>

             {/* Current defaults info for firm level */}
            {isFirmLevel && (
                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        <strong>System defaults:</strong> Strategic Context = {defaults.strategicContextDays} days, 
                        Financials = {defaults.financialsDays} days
                        {showAsana && `, Asana = ${defaults.asanaDays} days`}
                    </Typography>
                </Alert>
            )}

            {/* Save/Discard buttons */}
            {hasChanges && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="body2">
                            You have unsaved changes
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Button 
                                size="small" 
                                onClick={handleDiscard}
                                disabled={saving}
                            >
                                Discard
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </Box>
                </Alert>
            )}
        </Paper>
    );
};

export default AlertSettingsPanel;